/**
 * LollipopPreview Component
 * Renders lollipop charts using visx — horizontal or vertical lines terminated by circles
 */

import React, { useMemo } from "react";
import { scaleBand, scaleLinear } from "@visx/scale";
import { Group } from "@visx/group";
import { GridRows, GridColumns } from "@visx/grid";
import { AxisLeft, AxisBottom } from "@visx/axis";
import { useTheme } from "../../contexts/ThemeContext";
import { useData } from "../../contexts/DataContext";
import transformData from "../../utils/transformData";
import { darkenColor, getColors, getPrimaryColorPair, resolveColorByMapping } from "../../charts/shared/palettes";
import { deploymentProfile } from "../../config/deploymentProfile";
import { useChartTooltip } from "./useChartTooltip";
import AxisLaneDebugOverlay from './AxisLaneDebugOverlay';
import ChartTitle from './ChartTitle';
import ChartLegend from './ChartLegend';
import {
  resolveXAxisLabels,
  resolveYAxisLabels,
  computeChartLayout,
  computeAxisLanes,
  getAxisSpacingSpec,
  getXAxisBaselineNudge,
  isAxisDebugEnabled,
  reportAxisLaneClamp,
  computeTitleFontSize,
  formatCompactNumber,
  getCompactTickFormatter,
  LEGEND_SPACING, legendTopSpace, computeLegendRows,
} from '../../utils/chartLayout';

function LollipopPreview({ chartType, config, width, height }) {
  const { resolved: theme } = useTheme();
  const { isLoaded, derivedData: userData, mapping } = useData();
  const sampleConfig = useMemo(
    () => ({ numCategories: config.numCategories }),
    [config.numCategories],
  );

  // Get data — user data or sample data
  const rawData = useMemo(() => {
    if (isLoaded && userData.length > 0 && Object.keys(mapping).length > 0) {
      return transformData(userData, mapping, chartType.id);
    }
    if (chartType.getSampleData) {
      return chartType.getSampleData(sampleConfig);
    }
    return chartType.sampleData;
  }, [isLoaded, userData, mapping, chartType, sampleConfig]);

  // Sort data
  const data = useMemo(() => {
    const sorted = [...rawData];
    if (config.barOrdering === "ascending") {
      sorted.sort((a, b) => a.value - b.value);
    } else if (config.barOrdering === "descending") {
      sorted.sort((a, b) => b.value - a.value);
    }
    return sorted;
  }, [rawData, config.barOrdering]);

  const colorByResult = useMemo(
    () => resolveColorByMapping(data, config, theme),
    [data, config, theme],
  );
  const showLegend = colorByResult ? true : !!config.showLegend;
  const legendMarkerScale = theme.legendMarkerSize != null ? theme.legendMarkerSize / LEGEND_SPACING.rectSize : 1;
  const estimatedLegendRows = useMemo(() => {
    if (!showLegend || !colorByResult?.legendItems?.length) return 1;
    const { rowCount } = computeLegendRows(colorByResult.legendItems, Math.max(120, width - 160), theme.legendSize ?? 12, legendMarkerScale);
    return rowCount;
  }, [showLegend, colorByResult, width, theme.legendSize, legendMarkerScale]);

  const isHorizontal = config.orientation !== "vertical";
  const spacingSpec = getAxisSpacingSpec(theme);
  const {
    xTickGap,
    yTickGap,
    yTickBaselineNudge,
    xTickLength,
    yTickLength,
  } = spacingSpec;
  const xTickBaselineNudgeNumeric = getXAxisBaselineNudge({ tickType: 'numeric', spacingSpec });

  // Adaptive layout
  const categories = data.map(d => d.name);
  const xAxis = useMemo(() => {
    if (isHorizontal) return null;
    return resolveXAxisLabels({
      labels: categories,
      chartWidth: width,
      chartHeight: height,
      baseFontSize: theme.tickSize ?? 13,
      fontFamily: theme.fontBody,
      rotationOverride: config.labelRotation,
      truncateLabels: !!config.truncateXAxisLabels,
      estimatedMargins: 80,
    });
  }, [isHorizontal, categories, width, height, theme.tickSize, theme.fontBody, config.labelRotation, config.truncateXAxisLabels]);

  const { margin, innerWidth, innerHeight } = useMemo(() => computeChartLayout({
    width,
    height,
    title: config.title,
    titleFontSize: computeTitleFontSize(config.title, width) || undefined,
    xLabels: categories,
    tickFontSize: theme.tickSize ?? 13,
    tickFontFamily: theme.fontBody,
    hasXLabel: !!config.xlabel,
    hasYLabel: !!config.ylabel,
    orientation: isHorizontal ? 'horizontal' : 'vertical',
    baseRight: isHorizontal ? 60 : (config.showValues ? 60 : 30),
    baseBottom: isHorizontal ? 70 : 50,
    horizontalLeftMax: isHorizontal ? 180 : 160,
    xAxisResolved: isHorizontal ? undefined : xAxis,
    extra: { left: isHorizontal ? 0 : -15 },
    legend: showLegend ? {
      show: true,
      position: config.legendPosition || 'top',
      topSpace: legendTopSpace(estimatedLegendRows),
      bottomSpace: 80 + (estimatedLegendRows - 1) * LEGEND_SPACING.rowHeight,
      rightSpace: 150,
    } : undefined,
  }), [width, height, config.title, config.showValues, config.xlabel, config.ylabel, isHorizontal, xAxis, categories, theme.tickSize, theme.fontBody, showLegend, config.legendPosition, estimatedLegendRows]);

  // Scales
  const categoryScale = useMemo(() => {
    return scaleBand({
      domain: data.map((d) => d.name),
      range: isHorizontal ? [0, innerHeight] : [0, innerWidth],
      padding: 0.4,
    });
  }, [data, innerWidth, innerHeight, isHorizontal]);

  const valueScale = useMemo(() => {
    const maxVal = Math.max(...data.map((d) => d.value));
    const configMin = isHorizontal
      ? config.xMin && config.xMin.trim() !== ""
        ? parseFloat(config.xMin)
        : 0
      : config.yMin && config.yMin.trim() !== ""
        ? parseFloat(config.yMin)
        : 0;
    const configMax = isHorizontal
      ? config.xMax && config.xMax.trim() !== ""
        ? parseFloat(config.xMax)
        : maxVal * 1.1
      : config.yMax && config.yMax.trim() !== ""
        ? parseFloat(config.yMax)
        : maxVal * 1.1;

    return scaleLinear({
      domain: [configMin, configMax],
      range: isHorizontal ? [0, innerWidth] : [innerHeight, 0],
      nice: true,
    });
  }, [
    data,
    innerWidth,
    innerHeight,
    isHorizontal,
    config.xMin,
    config.xMax,
    config.yMin,
    config.yMax,
  ]);
  const yAxisResolved = useMemo(() => isHorizontal ? resolveYAxisLabels({
    labels: categories,
    innerHeight,
    baseFontSize: theme.tickSize ?? 13,
    fontFamily: theme.fontBody,
  }) : null, [isHorizontal, categories, innerHeight, theme.tickSize, theme.fontBody]);

  const yLanes = useMemo(() => {
    const tickLabels = isHorizontal ? categories : valueScale.ticks(5).map((v) => String(v));
    return computeAxisLanes({
      axisSide: 'left',
      labels: tickLabels,
      tickFontSize: theme.tickSize ?? 13,
      tickFontFamily: theme.fontBody,
      titleFontSize: theme.labelSize ?? 15,
      showTicks: !isHorizontal,
      tickLength: isHorizontal ? 0 : yTickLength,
      availableMargin: margin.left,
      spacingSpec: { ...spacingSpec, gapY: yTickGap },
      telemetryMeta: { chartId: `lollipop-${isHorizontal ? 'horizontal' : 'vertical'}`, viewportWidth: width, viewportHeight: height },
    });
  }, [isHorizontal, categories, valueScale, margin.left, theme, yTickGap, yTickLength, spacingSpec, width, height]);
  const xLanes = computeAxisLanes({
    axisSide: 'bottom',
    labels: isHorizontal ? valueScale.ticks(5).map((v) => String(v)) : (xAxis ? categories.map((c) => xAxis.tickFormat(c)) : categories),
    rotation: isHorizontal ? 0 : (xAxis?.rotation ?? 0),
    tickFontSize: isHorizontal ? (theme.tickSize ?? 13) : (xAxis?.fontSize ?? theme.tickSize ?? 13),
    tickFontFamily: theme.fontBody,
    titleFontSize: theme.labelSize ?? 15,
    showTicks: isHorizontal,
    tickLength: isHorizontal ? xTickLength : 0,
    spacingSpec: { ...spacingSpec, gapX: xTickGap },
    telemetryMeta: { chartId: `lollipop-${isHorizontal ? 'horizontal' : 'vertical'}`, viewportWidth: width, viewportHeight: height },
  });
  const axisDebugEnabled = isAxisDebugEnabled();
  if (axisDebugEnabled) {
    reportAxisLaneClamp(yLanes);
    reportAxisLaneClamp(xLanes);
  }

  // Colors
  const dotSize = config.dotSize || 10;
  const highlightDotSize = dotSize * 2;
  const lineThickness = config.lineThickness || 2;

  const baseColor = useMemo(() => {
    const colors = getColors(config, 1, theme);
    return colors[0] || config.color || "#4C6EE6";
  }, [config, theme]);

  const highlightColor = getPrimaryColorPair(config, theme).highlight;
  const mutedColor = useMemo(
    () => (deploymentProfile.isExternal ? darkenColor(baseColor, 0.08) : baseColor),
    [baseColor],
  );

  const decimals = config.valueDecimals ?? 1;

  // ─── Tooltip ───────────────────────────────────────────────────────────────
  const { containerRef, showTip, hideTooltip, Tip } = useChartTooltip(theme);

  // Axis styling
  const tickLabelProps = () => ({
    fill: theme.tickColor || theme.foreground || "#000",
    fontSize: theme.tickSize ?? 13,
    fontFamily: theme.fontBody || theme.fontHeading || "Cohere Text",
  });

  const axisLabelProps = {
    fill: theme.labelColor || theme.foreground || "#000",
    fontSize: theme.labelSize ?? 15,
    fontFamily: theme.fontBody || theme.fontHeading || "Cohere Text",
  };

  return (
    <div
      ref={containerRef}
      style={{
        width,
        height,
        position: "relative",
        backgroundColor: theme.background,
      }}
    >
      <ChartTitle title={config.title} chartWidth={width} marginLeft={margin.left} marginRight={margin.right} theme={theme} />

      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%" }} overflow="visible" onMouseLeave={hideTooltip}>
        <Group left={margin.left} top={margin.top}>
          {/* Grid */}
          {config.showGrid && isHorizontal && (
            <GridColumns
              scale={valueScale}
              height={innerHeight}
              stroke={theme.gridColor || "#CCCCCC"}
              strokeOpacity={theme.gridAlpha ?? 0.3}
              strokeDasharray="3 3"
            />
          )}
          {config.showGrid && !isHorizontal && (
            <GridRows
              scale={valueScale}
              width={innerWidth}
              stroke={theme.gridColor || "#CCCCCC"}
              strokeOpacity={theme.gridAlpha ?? 0.3}
              strokeDasharray="3 3"
            />
          )}

          {/* Lollipops */}
          {data.map((d, i) => {
            const isHighlighted = !colorByResult && config.highlightTop && i === 0;
            const currentDotSize = isHighlighted ? highlightDotSize : dotSize;
            const currentColor = colorByResult
              ? colorByResult.fillFn(d)
              : config.highlightTop
                ? isHighlighted
                  ? highlightColor
                  : mutedColor
                : baseColor;

            if (isHorizontal) {
              const cy =
                (categoryScale(d.name) || 0) + categoryScale.bandwidth() / 2;
              const cx = valueScale(d.value);

              return (
                <g key={`lollipop-${i}`}>
                  {/* Line */}
                  <line
                    x1={valueScale(0)}
                    y1={cy}
                    x2={cx}
                    y2={cy}
                    stroke={currentColor}
                    strokeWidth={
                      isHighlighted ? lineThickness + 0.5 : lineThickness
                    }
                  />
                  {/* Dot */}
                  <circle
                    cx={cx}
                    cy={cy}
                    r={currentDotSize / 2}
                    fill={currentColor}
                    style={{ cursor: "pointer" }}
                    onMouseMove={(e) => showTip(e, formatCompactNumber(d.value, decimals))}
                  />
                  {/* Value label */}
                  {config.showValues && (
                    <text
                      x={cx + currentDotSize / 2 + 6}
                      y={cy}
                      dominantBaseline="middle"
                      fontSize={isHighlighted ? 12 : 11}
                      fontWeight={isHighlighted ? "bold" : "normal"}
                      fill={theme.foreground || "#000"}
                      fontFamily={theme.fontBody || "Cohere Text"}
                    >
                      {formatCompactNumber(d.value, decimals)}
                    </text>
                  )}
                </g>
              );
            } else {
              // Vertical orientation
              const cx =
                (categoryScale(d.name) || 0) + categoryScale.bandwidth() / 2;
              const cy = valueScale(d.value);

              return (
                <g key={`lollipop-${i}`}>
                  {/* Line */}
                  <line
                    x1={cx}
                    y1={valueScale(0)}
                    x2={cx}
                    y2={cy}
                    stroke={currentColor}
                    strokeWidth={
                      isHighlighted ? lineThickness + 0.5 : lineThickness
                    }
                  />
                  {/* Dot */}
                  <circle
                    cx={cx}
                    cy={cy}
                    r={currentDotSize / 2}
                    fill={currentColor}
                    style={{ cursor: "pointer" }}
                    onMouseMove={(e) => showTip(e, formatCompactNumber(d.value, decimals))}
                  />
                  {/* Value label */}
                  {config.showValues && (
                    <text
                      x={cx}
                      y={cy - currentDotSize / 2 - 6}
                      textAnchor="middle"
                      fontSize={isHighlighted ? 12 : 11}
                      fontWeight={isHighlighted ? "bold" : "normal"}
                      fill={theme.foreground || "#000"}
                      fontFamily={theme.fontBody || "Cohere Text"}
                    >
                      {formatCompactNumber(d.value, decimals)}
                    </text>
                  )}
                </g>
              );
            }
          })}

          {/* Axes */}
          {isHorizontal ? (
            <>
              <AxisLeft
                scale={categoryScale}
                tickValues={yAxisResolved?.tickValues}
                stroke={theme.axisColor || "#000"}
                strokeWidth={theme.spineWidth ?? 1}
                tickStroke={theme.axisColor || "#000"}
                tickFormat={yAxisResolved?.tickFormat || ((val) => String(val))}
                tickLabelProps={(val) => {
                  const idx = data.findIndex(d => d.name === val);
                  const isHighlighted = config.highlightTop && idx === 0;
                  return {
                    ...tickLabelProps(),
                    textAnchor: 'end',
                    dx: -yTickGap,
                    dy: yTickBaselineNudge,
                    fontWeight: isHighlighted ? 'bold' : 'normal',
                    fontSize: isHighlighted ? (yAxisResolved?.fontSize || 13) : (yAxisResolved?.fontSize || 12),
                  };
                }}
                hideAxisLine={false}
                hideTicks
              />
              <AxisBottom
                scale={valueScale}
                top={innerHeight}
                stroke={theme.axisColor || "#000"}
                strokeWidth={theme.spineWidth ?? 1}
                tickStroke={theme.axisColor || "#000"}
                tickLength={xTickLength}
                tickFormat={getCompactTickFormatter(valueScale.domain()[1])}
                tickLabelProps={() => ({
                  ...tickLabelProps(),
                  textAnchor: "middle",
                  dy: xTickBaselineNudgeNumeric,
                })}
              />
            </>
          ) : (
            <>
              <AxisLeft
                scale={valueScale}
                stroke={theme.axisColor || "#000"}
                strokeWidth={theme.spineWidth ?? 1}
                tickStroke={theme.axisColor || "#000"}
                tickLength={yTickLength}
                tickFormat={getCompactTickFormatter(valueScale.domain()[1])}
                tickLabelProps={() => ({
                  ...tickLabelProps(),
                  dx: -yTickGap,
                  dy: yTickBaselineNudge,
                  textAnchor: "end",
                })}
              />
              <AxisBottom
                scale={categoryScale}
                top={innerHeight}
                stroke={theme.axisColor || "#000"}
                strokeWidth={theme.spineWidth ?? 1}
                tickStroke={theme.axisColor || "#000"}
                tickValues={xAxis ? xAxis.tickValues : undefined}
                tickFormat={xAxis ? xAxis.tickFormat : undefined}
                tickComponent={({ x, y, formattedValue }) => {
                  const rotation = xAxis?.rotation || 0;
                  const baseFontSize = xAxis?.fontSize ?? 13;
                  const idx = data.findIndex((d) => d.name === formattedValue || d.name === String(formattedValue));
                  const isHighlighted = config.highlightTop && idx === 0;
                  const fs = isHighlighted ? baseFontSize + 1 : baseFontSize;
                  const fw = isHighlighted ? "bold" : "normal";
                  const fill = theme.tickColor || theme.foreground || "#000";
                  const gap = 4;
                  if (!rotation) {
                    return (
                      <text x={x} y={y + gap} textAnchor="middle" dominantBaseline="hanging" fontSize={fs} fontWeight={fw} fill={fill}>
                        {formattedValue}
                      </text>
                    );
                  }
                  const ty = y + gap;
                  return (
                    <text x={x} y={ty} textAnchor="end" fontSize={fs} fontWeight={fw} fill={fill} transform={`rotate(-${rotation}, ${x}, ${ty})`}>
                      {formattedValue}
                    </text>
                  );
                }}
                tickLabelProps={() => ({ opacity: 0 })}
                hideAxisLine={false}
                hideTicks
              />
            </>
          )}

          {/* Axis labels */}
          {isHorizontal ? (
            <>
              {config.xlabel && (
                <text
                  x={innerWidth / 2}
                  y={innerHeight + xLanes.titleOffsetPx}
                  textAnchor="middle"
                  {...axisLabelProps}
                >
                  {config.xlabel}
                </text>
              )}
              {config.ylabel && (
                <text
                  x={-innerHeight / 2}
                  y={-yLanes.titleOffsetPx}
                  transform="rotate(-90)"
                  textAnchor="middle"
                  {...axisLabelProps}
                >
                  {config.ylabel}
                </text>
              )}
            </>
          ) : (
            <>
              {config.xlabel && (
                <text
                  x={innerWidth / 2}
                  y={innerHeight + xLanes.titleOffsetPx}
                  textAnchor="middle"
                  {...axisLabelProps}
                >
                  {config.xlabel}
                </text>
              )}
              {config.ylabel && (
                <text
                  x={-innerHeight / 2}
                  y={-yLanes.titleOffsetPx}
                  transform="rotate(-90)"
                  textAnchor="middle"
                  {...axisLabelProps}
                >
                  {config.ylabel}
                </text>
              )}
            </>
          )}
          {showLegend && colorByResult && (config.legendPosition || 'top') === 'top' && (
            <ChartLegend items={colorByResult.legendItems} innerWidth={innerWidth} theme={theme} position="top" />
          )}
          {showLegend && colorByResult && config.legendPosition === 'bottom' && (
            <ChartLegend items={colorByResult.legendItems} innerWidth={innerWidth} theme={theme} position="bottom" transform={`translate(0, ${innerHeight + LEGEND_SPACING.bottomGap})`} />
          )}
          {axisDebugEnabled && (
            <AxisLaneDebugOverlay
              lanes={[
                {
                  axisSide: 'left',
                  tickLabelOffsetPx: yLanes.tickLabelOffsetPx,
                  titleOffsetPx: yLanes.titleOffsetPx,
                  clamped: yLanes.clamped,
                  spineX: 0,
                  spineY1: 0,
                  spineY2: innerHeight,
                },
                {
                  axisSide: 'bottom',
                  tickLabelOffsetPx: xLanes.tickLabelOffsetPx,
                  titleOffsetPx: xLanes.titleOffsetPx,
                  clamped: xLanes.clamped,
                  spineY: innerHeight,
                  spineX1: 0,
                  spineX2: innerWidth,
                },
              ]}
            />
          )}
        </Group>

        {showLegend && colorByResult && config.legendPosition === 'right' && (
          <ChartLegend
            items={colorByResult.legendItems}
            innerWidth={innerWidth}
            theme={theme}
            position="right"
            transform={`translate(${margin.left + innerWidth + LEGEND_SPACING.rightGap}, ${margin.top + innerHeight / 2 - 12})`}
          />
        )}
      </svg>
      <Tip />
    </div>
  );
}

export default LollipopPreview;
